import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import { useQuery } from "@tanstack/react-query";
import { Field, Form, useFormikContext } from "formik";
import { useMemo, type FC } from "react";
import { AutocompleteField, FormTextField, MultiAutocompleteField } from "src/components/UI/Form";
import { vendors } from "src/queries/vendors";
import type { Option } from "src/types/option";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import type { StoreAndPartnerSelection } from "../vc-account-assignments-types";

// Use string partner IDs so that we can recognize the "No partner" option with an empty string,
// while keeping the empty value as `null`

const NO_PARTNER_OPTION: Option = { value: "", title: "No partner" };

export interface StoreAndPartnerSelectionFormProps {
  onCancel(): void;
}

export const StoreAndPartnerSelectionForm: FC<StoreAndPartnerSelectionFormProps> = ({ onCancel }) => {
  const { setFieldValue, values, submitForm } = useFormikContext<StoreAndPartnerSelection>();

  const partnersQuery = useQuery(vendors.referralPartners);
  const partnerOptions = useMemo<Option[]>(
    () => [
      NO_PARTNER_OPTION,
      ...(partnersQuery.data?.map(({ id, name }) => ({ value: id.toString(), title: name })) ?? EMPTY_ARRAY),
    ],
    [partnersQuery.data],
  );

  return (
    <>
      <DialogTitle style={{ border: "none" }}>Select stores to assign</DialogTitle>
      <div style={{ paddingInline: "1.5rem", paddingTop: "1rem", paddingBottom: "1rem" }}>
        {/* prevent submission when pressing enter key on inputs */}
        {/* only allow submission through "submitForm" (ie the Continue button) */}
        <Form onSubmit={e => e.preventDefault()}>
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ marginBottom: "1rem" }}>
              <p style={{ fontWeight: 500, marginBottom: "0.75rem" }}>Enter the vendor name for these accounts</p>
              <Field name="vendorName" component={FormTextField} label="Vendor Name" size="small" fullWidth />
            </div>
            <div>
              <div style={{ lineHeight: 1.2, marginBottom: "1rem" }}>
                <p style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
                  Copy and paste the stores you are assigning in the below field
                </p>
                <p style={{ opacity: 0.8 }}>
                  <i>In Google Sheets or Excel, select and copy all store names at once, then paste them here</i>
                </p>
              </div>
              <Field
                name="storeNames"
                label="Store names"
                placeholder="Paste store names here"
                component={MultiAutocompleteField}
                options={EMPTY_ARRAY}
                disableSuggestions
                disableCommaSeparation
                variant="outlined"
                setFieldValue={setFieldValue}
              />
            </div>
            <div>
              <p style={{ fontWeight: 500, lineHeight: 1.2, marginBlock: "1rem" }}>
                If these stores are associated to a partner, select the partner below
              </p>
              <Field
                name="partnerId"
                component={AutocompleteField}
                label={
                  partnersQuery.isLoading
                    ? "Loading partners..."
                    : partnersQuery.isError
                      ? "Error loading partners"
                      : "Partner Selection"
                }
                options={partnerOptions}
                getOptionLabel={(option: Option) => option.title}
              />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={!values.vendorName || values.storeNames.length === 0 || values.partnerId === null}
              onClick={submitForm}
            >
              Continue
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};
